import { useEffect, useRef, useState } from 'react';

const products = [
  {
    id: 'vpn',
    name: 'VPN',
    tagline: 'Private & Secure Browsing',
    description:
      'Military-grade encryption protects your data on any network. Browse privately, bypass restrictions, and stay secure on public Wi-Fi with our fast and reliable VPN.',
    features: ['AES-256 Encryption', 'Zero-Log Policy', 'Global Servers', 'Kill Switch'],
    image:
      'https://public.readdy.ai/ai/img_res/238632ac37d70c805c366ef0cc68465e.jpg',
    accent: '#22c55e',
  },
  {
    id: 'cleaner',
    name: 'Cleaner',
    tagline: 'Peak Device Performance',
    description:
      'Smart junk removal, duplicate photo detection, and memory optimization tools keep your iPhone running at its best. Reclaim storage in seconds.',
    features: ['Junk File Removal', 'Duplicate Finder', 'Memory Boost', 'Large File Scanner'],
    image:
      'https://readdy.ai/api/search-image?query=clean%20minimal%20white%20glowing%20interface%20iPhone%20cleaner%20app%20concept%20sparkle%20light%20particles%20speed%20optimization%20abstract%20modern%20dark%20background%20digital%20art%20ultra%20hd&width=600&height=800&seq=product-cleaner-melti-001&orientation=portrait',
    accent: '#f59e0b',
  },
];

export default function ProductsSection() {
  const [visible, setVisible] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="products"
      ref={ref}
      className="w-full bg-white py-24 md:py-36 px-6 md:px-10"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`mb-14 md:mb-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-gray-400 text-xs tracking-[0.4em] uppercase mb-4">
            iOS Ecosystem
          </p>
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 900,
              fontSize: 'clamp(2rem, 4vw, 3.8rem)',
              lineHeight: 1.1,
              color: '#111',
            }}
          >
            Our Products
          </h2>
          <p className="text-gray-500 text-base mt-4 max-w-lg font-300" style={{ fontWeight: 300 }}>
            Two flagship iOS applications built to protect and optimize your mobile experience.
          </p>
        </div>

        {/* Tab Switcher */}
        <div
          className={`flex gap-2 mb-10 transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex bg-gray-100 rounded-full p-1">
            {products.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setActiveIdx(i)}
                className={`px-6 py-2 rounded-full text-sm font-500 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeIdx === i
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
                style={{ fontWeight: 500 }}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>

        {/* Product Card */}
        {products.map((product, i) => (
          <div
            key={product.id}
            className={`transition-all duration-500 ${
              activeIdx === i ? 'block opacity-100' : 'hidden opacity-0'
            }`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              {/* Left Content */}
              <div className="order-2 lg:order-1">
                <div
                  className="inline-block px-3 py-1 rounded-full text-xs font-600 mb-6"
                  style={{
                    backgroundColor: `${product.accent}18`,
                    color: product.accent,
                    fontWeight: 600,
                  }}
                >
                  {product.tagline}
                </div>
                <h3
                  className="text-gray-900 mb-4"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 700,
                    fontSize: 'clamp(1.8rem, 3vw, 3rem)',
                    lineHeight: 1.15,
                  }}
                >
                  {product.name}
                </h3>
                <p className="text-gray-500 text-base leading-relaxed mb-8 font-300" style={{ fontWeight: 300 }}>
                  {product.description}
                </p>
                {/* Features List */}
                <ul className="grid grid-cols-2 gap-3">
                  {product.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-2 text-gray-700 text-sm">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i
                          className="ri-check-line text-sm"
                          style={{ color: product.accent }}
                        ></i>
                      </div>
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right Image */}
              <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
                <div className="relative w-64 md:w-80 lg:w-72 xl:w-80">
                  {/* Phone Frame */}
                  <div className="relative rounded-3xl overflow-hidden aspect-[3/5] w-full">
                    <img
                      src={product.image}
                      alt={`${product.name} iOS app`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    {/* App Label Overlay */}
                    <div className="absolute bottom-6 left-6 right-6">
                      <span className="text-white text-2xl font-800" style={{ fontWeight: 800 }}>
                        {product.name}
                      </span>
                      <p className="text-white/70 text-xs mt-1">{product.tagline}</p>
                    </div>
                  </div>
                  {/* Glow */}
                  <div
                    className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl -z-10"
                    style={{ backgroundColor: product.accent }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
